import { useCallback, useState } from 'react'
import { getSunoPromptTracks, type SunoPrompt } from '../../../api/suno'
import { useAudioPlayer, type PlayableTrack } from '../../audio/useAudioPlayer'

type PromptLibraryPlayback = ReturnType<typeof useAudioPlayer> & {
  playbackLoadingId: string | null
  playbackPromptId: string | null
  playbackTracks: PlayableTrack[]
  playPromptTracks: (prompt: SunoPrompt) => Promise<void>
  playPromptNext: () => void
}

export const usePromptLibraryPlayback = (): PromptLibraryPlayback => {
  const audioPlayer = useAudioPlayer()
  const { playingTrack, playTrack, togglePlayback } = audioPlayer
  const [playbackTracks, setPlaybackTracks] = useState<PlayableTrack[]>([])
  const [playbackPromptId, setPlaybackPromptId] = useState<string | null>(null)
  const [playbackLoadingId, setPlaybackLoadingId] = useState<string | null>(null)

  const playPromptTracks = useCallback(async (prompt: SunoPrompt): Promise<void> => {
    if (
      playbackPromptId === prompt.id
      && playingTrack
      && playbackTracks.some((track) => track.id === playingTrack.id)
    ) {
      togglePlayback()
      return
    }

    setPlaybackLoadingId(prompt.id)

    try {
      const result = await getSunoPromptTracks(prompt.id, { pageSize: 200 })
      const readyTracks = result.linked
        .filter((track) => track.status === 'ready')
        .map((track) => ({
          id: track.id,
          title: track.title,
          artist: track.artist,
          file_url: track.file_url,
          duration_seconds: track.duration_seconds
        }))

      if (readyTracks.length === 0) {
        return
      }

      const firstTrack = readyTracks[0] as PlayableTrack
      setPlaybackTracks(readyTracks)
      setPlaybackPromptId(prompt.id)
      playTrack(firstTrack)
    } catch {
      // Playback fetch failure is non-fatal.
    } finally {
      setPlaybackLoadingId(null)
    }
  }, [playTrack, playbackPromptId, playbackTracks, playingTrack, togglePlayback])

  const playPromptNext = useCallback((): void => {
    if (!playingTrack || playbackTracks.length === 0) return

    const currentIndex = playbackTracks.findIndex((track) => track.id === playingTrack.id)
    if (currentIndex === -1) return

    const nextTrack = playbackTracks[(currentIndex + 1) % playbackTracks.length]
    if (nextTrack) {
      playTrack(nextTrack)
    }
  }, [playTrack, playbackTracks, playingTrack])

  return {
    ...audioPlayer,
    playbackLoadingId,
    playbackPromptId,
    playbackTracks,
    playPromptTracks,
    playPromptNext
  }
}
